/**
 * EliteDeepDiveCredit — small card that surfaces the Elite user's
 * 1-per-cycle included Deep-Dive plus the $5 add-on pricing.
 *
 * Shown inside AnalysisModal for authenticated users. Handles three
 * states:
 *   1. Elite with credits → "Redeem included Deep-Dive"
 *   2. Elite without credits → "Buy Deep-Dive · $5" (add-on price)
 *   3. Non-Elite (or unauthenticated with no entitlement) → hidden
 *      (existing TripwireOffer handles $7 checkout for anonymous users)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Download, Crown, FileText } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { API } from "../lib/helpers";

export default function EliteDeepDiveCredit({ analysisId, isAuthenticated }) {
  const [ent, setEnt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/deep-dive/entitlement`)
      .then(r => { if (!cancelled) setEnt(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (loading || !isAuthenticated) return null;
  // No entitlement doc AND non-Elite → let the retail tripwire show instead.
  if (!ent?.has_entitlement) return null;

  const remaining = ent.credits_remaining || 0;
  const addonPrice = ent.addon_price_usd || 5;
  const included = ent.credits_included || 1;

  const handleRedeem = async () => {
    if (!analysisId) {
      toast.error("Save this analysis before redeeming your Deep-Dive.");
      return;
    }
    setRedeeming(true);
    try {
      const r = await axios.post(`${API}/deep-dive/redeem`, { analysis_id: analysisId });
      setEnt(prev => prev ? { ...prev, credits_redeemed: prev.credits_redeemed + 1, credits_remaining: prev.credits_remaining - 1 } : prev);
      if (r.data?.download_url) {
        window.open(r.data.download_url, "_blank");
        toast.success("Deep-Dive unlocked — download starting");
      } else {
        toast.success("Deep-Dive credit redeemed — save the analysis to download the PDF");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.error === "no_credits_left") {
        toast.info(`You've used your ${included} Deep-Dive this cycle — buy the $${addonPrice} add-on below`);
        setEnt(prev => prev ? { ...prev, credits_redeemed: included, credits_remaining: 0 } : prev);
      } else {
        toast.error(detail?.message || "Could not redeem Deep-Dive");
      }
    } finally {
      setRedeeming(false);
    }
  };

  const handleAddonCheckout = async () => {
    if (!analysisId) {
      toast.error("Save this analysis before purchasing an add-on Deep-Dive.");
      return;
    }
    setRedeeming(true);
    try {
      const r = await axios.post(`${API}/deep-dive/addon-checkout`, {
        analysis_id: analysisId,
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || "Could not start checkout");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-transparent p-3 sm:p-4"
      data-testid="elite-deep-dive-card"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2">
          <Crown className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-bold text-foreground flex items-center gap-1.5">
              Investor Deep-Dive
              {remaining > 0 ? (
                <Badge className="text-[9px] px-1 py-0 bg-emerald-500/20 text-emerald-500 border-0">
                  {remaining} INCLUDED
                </Badge>
              ) : (
                <Badge className="text-[9px] px-1 py-0 bg-muted text-muted-foreground border-0">
                  0 REMAINING THIS CYCLE
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {remaining > 0
                ? `Multi-year projections, sensitivity analysis, and break-even scenarios in a downloadable PDF.`
                : `Your next Deep-Dive is available on ${new Date(ent.period_end).toLocaleDateString()}, or grab an add-on for $${addonPrice}.`}
            </p>
          </div>
        </div>
      </div>

      {remaining > 0 ? (
        <Button
          size="sm"
          className="w-full h-8 bg-emerald-500 hover:bg-emerald-600 text-black font-bold text-xs"
          onClick={handleRedeem}
          disabled={redeeming}
          data-testid="deep-dive-redeem-btn"
        >
          <Sparkles className="w-3.5 h-3.5 mr-1" />
          {redeeming ? "Unlocking…" : "Redeem Included Deep-Dive"}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="w-full h-8 border-amber-500/40 text-amber-500 hover:bg-amber-500/10 font-medium text-xs"
          onClick={handleAddonCheckout}
          disabled={redeeming}
          data-testid="deep-dive-addon-btn"
        >
          <FileText className="w-3.5 h-3.5 mr-1" />
          {redeeming ? "Redirecting…" : `Buy Add-On Deep-Dive · $${addonPrice}`}
        </Button>
      )}
    </div>
  );
}
